export const RCON_QPM_PENDING = "pending instrumentation";

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let idx = 0;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx++;
  }
  const fixed = value >= 10 || idx === 0 ? 0 : 1;
  return `${value.toFixed(fixed)} ${units[idx]}`;
}

export function formatAge(ageSeconds: number | undefined): string {
  if (ageSeconds === undefined) return "—";
  if (ageSeconds < 60) return `${ageSeconds}s`;
  const min = Math.floor(ageSeconds / 60);
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h`;
}

export function formatCpuLoadSubtext(cores: number | string | undefined): string {
  return `${cores ?? "?"} Cores · Load Average`;
}

export function meterLevel(value: number | null): "ok" | "warn" | "crit" | "na" {
  if (value === null) return "na";
  if (value >= 90) return "crit";
  if (value >= 75) return "warn";
  return "ok";
}

export function serverRconQpmLabel(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : String(value);
}

export function qpmBarPercent(value: number | null | undefined, max: number): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0 || max <= 0) return 0;
  return Math.max(4, Math.min(100, Math.round((n / max) * 100)));
}

export interface DashboardSparkSample {
  cpuLoadPct?: number;
  ramPct?: number;
  diskPct?: number;
  rconQpm?: number;
}

export interface DashboardSparkHistory {
  cpuLoadPct: number[];
  ramPct: number[];
  diskPct: number[];
  rconQpm: number[];
}

export function emptyDashboardSparkHistory(): DashboardSparkHistory {
  return { cpuLoadPct: [], ramPct: [], diskPct: [], rconQpm: [] };
}

function clampPercent(value: unknown): number | undefined {
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(100, n));
}

export function cpuLoadGraphPercent(load1: unknown, cores: unknown): number | undefined {
  const load = Number(load1);
  const coreCount = Number(cores);
  if (!Number.isFinite(load)) return undefined;
  if (Number.isFinite(coreCount) && coreCount > 0) return clampPercent((load / coreCount) * 100);
  return undefined;
}

function appendValue(values: number[], next: number | undefined, maxPoints: number): number[] {
  if (next === undefined) return values.slice(-maxPoints);
  return [...values, next].slice(-maxPoints);
}

export function appendDashboardSparkSample(
  history: DashboardSparkHistory,
  sample: DashboardSparkSample,
  maxPoints = 60,
): DashboardSparkHistory {
  return {
    cpuLoadPct: appendValue(history.cpuLoadPct, clampPercent(sample.cpuLoadPct), maxPoints),
    ramPct: appendValue(history.ramPct, clampPercent(sample.ramPct), maxPoints),
    diskPct: appendValue(history.diskPct, clampPercent(sample.diskPct), maxPoints),
    rconQpm: appendValue(history.rconQpm, Number.isFinite(sample.rconQpm) ? Number(sample.rconQpm) : undefined, maxPoints),
  };
}

export function cacheAgeState(online: boolean, ageSeconds: number | undefined): "fresh" | "stale" | "offline" {
  if (!online) return "offline";
  if (ageSeconds === undefined) return "stale";
  if (ageSeconds <= 20) return "fresh";
  if (ageSeconds <= 90) return "stale";
  return "offline";
}

/**
 * Löst den per-Server Signal-Zeitpunkt (epoch ms) für "Last Signal" auf:
 * 1. echter per-Server WS-Push (`wsSignalAt`) — Vorrang, altert unabhängig.
 * 2. sonst Rekonstruktion aus dem Poll-Age dieses Servers (`metricsAt - ageSec`),
 *    NUR als Initial-Fallback bis der erste Push für diesen Server kommt.
 * `metricsAt` ist KEINE Signalzeit — es ist nur der Anker, von dem aus das
 * server-individuelle `ageSec` zurückgerechnet wird (jeder Server hat sein
 * eigenes ageSec, daher kein pauschales Gleichstempeln).
 * Gibt `null`, wenn keine Quelle vorhanden ist.
 */
export function resolveSignalAtMs(
  wsSignalAt: number | undefined,
  metricsAt: number | null,
  ageSec: number | undefined,
): number | null {
  if (wsSignalAt !== undefined) return wsSignalAt;
  if (metricsAt !== null && ageSec !== undefined && ageSec >= 0) return metricsAt - ageSec * 1000;
  return null;
}

export type SignalAgeTone = "fresh" | "warn" | "hot" | "bad" | "none";

/**
 * Freshness-/Heat-Ton für "Last Signal" anhand des Alters in Sekunden:
 * - <= 1s  → fresh (grün)
 * - <= 3s  → warn  (amber)
 * - < 5s   → hot   (orange)
 * - >= 5s  → bad   (rot)
 * `undefined`/negativ → none (neutral, kein Signal-Alter bekannt).
 */
export function signalAgeTone(ageSec: number | undefined): SignalAgeTone {
  if (ageSec === undefined || ageSec < 0) return "none";
  if (ageSec <= 1) return "fresh";
  if (ageSec <= 3) return "warn";
  if (ageSec < 5) return "hot";
  return "bad";
}

export function cacheAgeLabel(online: boolean, ageSeconds: number | undefined): string {
  const state = cacheAgeState(online, ageSeconds);
  if (state === "fresh") return "fresh";
  if (state === "stale") return "stale";
  return "offline";
}

export interface DashboardTelemetryRow {
  online: boolean;
  ageSeconds?: number;
  rconQpm?: number | null;
  rconErrorsPerMinute?: number | null;
  lastRconError?: string | null;
  lastRconOkAge?: number;
}

export type RconHealthState = "ok" | "server-down" | "backoff" | "stale" | "no-data";
export type RconHealthTone = "ok" | "down" | "stale" | "no-data";

export function rconHealthState(row: DashboardTelemetryRow): RconHealthState {
  const error = (row.lastRconError ?? "").toLowerCase();
  const hasError = Boolean(error) || Number(row.rconErrorsPerMinute ?? 0) > 0;
  const hasMetric = row.rconQpm !== null && row.rconQpm !== undefined;
  const hasSignal = row.ageSeconds !== undefined || hasMetric || row.lastRconOkAge !== undefined || hasError;
  if (!hasSignal) return "no-data";
  if (error.includes("backoff") || error.includes("reconnect") || error.includes("retry") || error.includes("wait")) {
    return "backoff";
  }
  const age = cacheAgeState(row.online, row.ageSeconds);
  if (age === "offline") {
    if (!row.online && !hasMetric && !hasError) return "no-data";
    if (!row.online) return "server-down";
    return "stale";
  }
  if (age === "stale") return "stale";
  if (!row.online) return "server-down";
  return "ok";
}

export function rconHealthLabel(state: RconHealthState): string {
  if (state === "server-down") return "SERVER DOWN";
  if (state === "backoff") return "SERVER DOWN";
  if (state === "stale") return "STALE";
  if (state === "no-data") return "NO DATA";
  return "OK";
}

export function rconHealthTone(state: RconHealthState): RconHealthTone {
  if (state === "server-down" || state === "backoff") return "down";
  if (state === "stale") return "stale";
  if (state === "no-data") return "no-data";
  return "ok";
}

export function summarizeDashboardMatrix(rows: DashboardTelemetryRow[]) {
  return rows.reduce(
    (acc, row) => {
      acc.total += 1;
      if (row.online) acc.live += 1;
      else acc.offline += 1;
      const qpm = Number(row.rconQpm ?? 0);
      if (Number.isFinite(qpm) && qpm > 0) acc.qpm += qpm;
      const health = rconHealthState(row);
      if (health === "server-down" || health === "backoff") acc.down += 1;
      if (health === "stale") acc.degraded += 1;
      return acc;
    },
    { total: 0, live: 0, offline: 0, qpm: 0, down: 0, degraded: 0 },
  );
}

export interface DashboardFallbackServer {
  id: string;
  name: string;
}

export interface DashboardFallbackOverview {
  map: string;
  mode: string;
  players: number;
  maxPlayers: number;
  ts: number;
}

export function fallbackDashboardServers(
  servers: DashboardFallbackServer[],
  overview: Record<string, DashboardFallbackOverview>,
  nowSeconds = Math.floor(Date.now() / 1000),
) {
  return servers.map((s) => {
    const o = overview[s.id];
    const ageSeconds = o?.ts ? Math.max(0, nowSeconds - o.ts) : undefined;
    return {
      id: s.id,
      name: s.name,
      online: ageSeconds !== undefined && ageSeconds < 20,
      map: o?.map || undefined,
      mode: o?.mode || undefined,
      players: o ? o.players : undefined,
      maxPlayers: o ? o.maxPlayers : undefined,
      ageSeconds,
      rconQpm: null,
      rconErrorsPerMinute: 0,
      lastRconError: null,
    };
  });
}
